import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

export interface VaultAccessModalProps {
  vault: { id: string; name: string };
  onClose: () => void;
}

export function VaultAccessModal({ vault, onClose }: VaultAccessModalProps) {
  const [members, setMembers] = useState<any[]>([]);
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('consumer');
  const [adding, setAdding] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [membersRes, orgRes] = await Promise.all([
        api.getVaultMembers(vault.id),
        api.listOrgMembers()
      ]);
      setMembers(membersRes.members || []);
      setOrgMembers(orgRes.members || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [vault.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setAdding(true);
    try {
      await api.addVaultMember(vault.id, selectedUserId, selectedRole);
      setSelectedUserId('');
      loadData();
    } catch (e: any) {
      alert('Lỗi: ' + (e.message || 'Unknown error'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!confirm('Bạn có chắc muốn xoá nhân viên này khỏi Vault?')) return;
    try {
      await api.removeVaultMember(vault.id, userId);
      loadData();
    } catch (e: any) {
      alert('Lỗi: ' + (e.message || 'Unknown error'));
    }
  };

  const availableOrgMembers = orgMembers.filter(orgM => !members.some(m => m.user_id === orgM.id));

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '550px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>⚙️ Phân quyền Vault</h2>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Vault: <strong>{vault.name}</strong>
            </div>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>✕</button>
        </div>

        {/* Form thêm thành viên */}
        <div style={{ background: 'var(--bg-elevated)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', margin: '0 0 12px 0' }}>Thêm thành viên vào Kho báu</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select 
              className="form-input" 
              style={{ flex: 1 }} 
              value={selectedUserId} 
              onChange={e => setSelectedUserId(e.target.value)}
            >
              <option value="">-- Chọn nhân viên từ Tổ chức --</option>
              {availableOrgMembers.map(m => (
                <option key={m.id} value={m.id}>{m.display_name} ({m.role})</option>
              ))}
            </select>
            <select 
              className="form-input" 
              style={{ width: '130px' }} 
              value={selectedRole} 
              onChange={e => setSelectedRole(e.target.value)}
            >
              <option value="admin">Admin</option>
              <option value="contributor">Contributor</option>
              <option value="consumer">Consumer</option>
            </select>
            <button className="btn btn--primary" onClick={handleAdd} disabled={!selectedUserId || adding}>
              {adding ? '...' : 'Thêm'}
            </button>
          </div>
          {availableOrgMembers.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Không còn nhân viên nào trong Tổ chức chưa được thêm.
            </div>
          )}
        </div>

        {/* Danh sách thành viên */}
        <h3 style={{ fontSize: '14px', margin: '0 0 12px 0' }}>Thành viên hiện tại</h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}><div className="spinner"></div></div>
        ) : members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', background: 'var(--bg-elevated)', borderRadius: '12px', border: '1px dashed var(--border)', color: 'var(--text-muted)' }}>
            Chưa có thành viên nào
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
            {members.map(m => (
              <div key={m.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '14px' }}>{m.display_name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>ID: {m.user_id}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className={`badge ${m.role === 'admin' ? 'badge--purple' : m.role === 'contributor' ? 'badge--blue' : 'badge--gray'}`}>
                    {m.role}
                  </span>
                  <button 
                    onClick={() => handleRemove(m.user_id)}
                    style={{
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                      color: '#f87171',
                      padding: '5px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    🗑 Xoá
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
